import { SkeletonCircle } from '@chakra-ui/react';
import EmailIcon from '@mui/icons-material/Email';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import WorkspacesOutlined from '@mui/icons-material/WorkspacesOutlined';
import UserInitials from '@/trio-ui/UserInitials';
import { PersonType } from '@/types/api';
import { JobInfo } from '@/types/api/staffMember';
import { formatDateDistance } from '@/utils/dates';
import { formatPTORequestDate } from '@/utils/pto';
import { getMostRecentStaffMemberActiveJob } from '@/utils/staffMember';
import * as Styles from './HomeSidePanel.styles';
import CandidateProfilePictureManager from '../CandidateProfilePictureManager';

type HomeSidePanelProps = {
  id?: string;
  firstName?: string;
  lastName?: string;
  pictureUrl?: string;
  developerType?: string;
  jobInfo?: JobInfo[];
  department?: string;
  team?: string;
  email?: string;
  mobilePhone?: string;
  linkedinUrl?: string;
  trioProfileUrl?: string;
  hireDate?: string;
  managerFirstName?: string;
  managerLastName?: string;
  managerPicture?: string;
  managerType?: string;
  teamMembers?: Pick<PersonType, 'id' | 'firstName' | 'lastName' | 'pictureUrl'>[];
  isLoadingStaffMember?: boolean;
  isLoadingTeamMembers?: boolean;
  refreshUser?: () => void;
};

const HomeSidePanel = ({
  id,
  firstName,
  lastName,
  pictureUrl,
  jobInfo,
  department,
  team,
  email,
  mobilePhone,
  linkedinUrl,
  trioProfileUrl,
  hireDate,
  managerFirstName,
  managerLastName,
  managerPicture,
  managerType,
  teamMembers = [],
  isLoadingStaffMember = false,
  isLoadingTeamMembers = false,
  refreshUser,
  ...props
}: HomeSidePanelProps): JSX.Element => {
  const monstRecentStaffMemberJob = getMostRecentStaffMemberActiveJob(jobInfo || []);
  return (
    <Styles.HomeSidePanel {...props}>
      <Styles.UserSection>
        <CandidateProfilePictureManager
          id={id || ''}
          firstName={firstName || ''}
          lastName={lastName}
          pictureUrl={pictureUrl || ''}
          pictureIsEditable
          refetchCandidate={refreshUser}
          isFromHome
        />
        <div>
          <Styles.UserName>
            {firstName} {lastName}
          </Styles.UserName>
          {monstRecentStaffMemberJob?.title && (
            <Styles.UserTitle>{monstRecentStaffMemberJob?.title}</Styles.UserTitle>
          )}
        </div>
      </Styles.UserSection>

      {(department || team || isLoadingStaffMember || isLoadingTeamMembers) && (
        <div>
          <Styles.LabelWithIconList>
            {department && (
              <Styles.LabelWithIcon.Container>
                <Styles.LabelWithIcon.Icon>
                  <HomeWorkOutlined />
                </Styles.LabelWithIcon.Icon>
                <Styles.Text>{department}</Styles.Text>
              </Styles.LabelWithIcon.Container>
            )}
            {(team || isLoadingStaffMember) && (
              <Styles.LabelWithIcon.Container>
                <Styles.LabelWithIcon.Icon>
                  <WorkspacesOutlined />
                </Styles.LabelWithIcon.Icon>
                {isLoadingStaffMember ? (
                  <Styles.LabelWithIcon.Skeleton width="40%" />
                ) : (
                  <Styles.Text>{team}</Styles.Text>
                )}
              </Styles.LabelWithIcon.Container>
            )}
            {(isLoadingStaffMember || isLoadingTeamMembers || teamMembers.length > 0) && (
              <Styles.TeamMembersList>
                {isLoadingStaffMember || isLoadingTeamMembers ? (
                  <>
                    <SkeletonCircle size="6" />
                    <SkeletonCircle size="6" />
                    <SkeletonCircle size="6" />
                  </>
                ) : (
                  teamMembers.map((member) => (
                    <Styles.TeamMemberThumbnail
                      key={member.id}
                      firstName={member.firstName}
                      lastName={member.lastName}
                      pictureUrl={member.pictureUrl || undefined}
                      size={24}
                    />
                  ))
                )}
              </Styles.TeamMembersList>
            )}
          </Styles.LabelWithIconList>
        </div>
      )}

      {(email || mobilePhone || linkedinUrl || trioProfileUrl) && (
        <div>
          <Styles.LabelWithIconList>
            {email && (
              <Styles.LabelWithIcon.Container>
                <Styles.LabelWithIcon.Icon>
                  <EmailIcon />
                </Styles.LabelWithIcon.Icon>
                <Styles.Text>{email}</Styles.Text>
              </Styles.LabelWithIcon.Container>
            )}
            {mobilePhone && (
              <Styles.LabelWithIcon.Container>
                <Styles.LabelWithIcon.Icon>
                  <LocalPhoneIcon />
                </Styles.LabelWithIcon.Icon>
                <Styles.Text>{mobilePhone}</Styles.Text>
              </Styles.LabelWithIcon.Container>
            )}
            {linkedinUrl && (
              <a href={linkedinUrl} target="_blank" rel="noreferrer">
                <Styles.LabelWithIcon.Container>
                  <Styles.LabelWithIcon.Icon>
                    <LinkedInIcon />
                  </Styles.LabelWithIcon.Icon>
                  <Styles.Text>Visit LinkedIn profile</Styles.Text>
                </Styles.LabelWithIcon.Container>
              </a>
            )}
            {trioProfileUrl && (
              <a href={trioProfileUrl} target="_blank" rel="noreferrer">
                <Styles.LabelWithIcon.Container>
                  <Styles.LabelWithIcon.Icon>
                    <LinkIcon />
                  </Styles.LabelWithIcon.Icon>
                  <Styles.Text>Visit Trio profile</Styles.Text>
                </Styles.LabelWithIcon.Container>
              </a>
            )}
          </Styles.LabelWithIconList>
        </div>
      )}
      {(hireDate || isLoadingStaffMember) && (
        <div>
          <Styles.SectionTitle>Hire Date</Styles.SectionTitle>
          {isLoadingStaffMember && (
            <>
              <Styles.HireDateSkeletonContainer>
                <Styles.HireDateSkeleton width="40%" />
              </Styles.HireDateSkeletonContainer>
              <Styles.HireDateSkeletonContainer>
                <Styles.HireDateSkeleton width="60%" />
              </Styles.HireDateSkeletonContainer>
            </>
          )}
          {!isLoadingStaffMember && hireDate && (
            <>
              <Styles.Text>{formatPTORequestDate(new Date(hireDate))}</Styles.Text>
              <Styles.Text>{formatDateDistance(new Date(hireDate))}</Styles.Text>
            </>
          )}
        </div>
      )}
      {(isLoadingStaffMember || managerFirstName) && (
        <div>
          <Styles.ManagerSectionTitle>Manager</Styles.ManagerSectionTitle>
          <Styles.ManagerContainer>
            {isLoadingStaffMember && (
              <Styles.ManagerThumbnailContainer>
                <SkeletonCircle size="6" />
              </Styles.ManagerThumbnailContainer>
            )}
            {managerFirstName && !isLoadingStaffMember && (
              <Styles.ManagerThumbnailContainer>
                <UserInitials
                  firstName={managerFirstName}
                  lastName={managerLastName}
                  pictureUrl={managerPicture}
                  size={24}
                />
              </Styles.ManagerThumbnailContainer>
            )}
            <Styles.ManagerTextBox>
              {isLoadingStaffMember ? (
                <>
                  <Styles.HireDateSkeletonContainer>
                    <Styles.HireDateSkeleton width="40%" />
                  </Styles.HireDateSkeletonContainer>
                  <Styles.HireDateSkeletonContainer>
                    <Styles.HireDateSkeleton width="50%" />
                  </Styles.HireDateSkeletonContainer>
                </>
              ) : (
                <>
                  <Styles.ManagerName>
                    {managerFirstName} {managerLastName}
                  </Styles.ManagerName>
                  {managerType && <Styles.Text>{managerType}</Styles.Text>}
                </>
              )}
            </Styles.ManagerTextBox>
          </Styles.ManagerContainer>
        </div>
      )}
      {/* <div>
        <Styles.EditProfileButton>
          <EditIcon /> (@mui/icons-material/Edit)
          Edit Profile
        </Styles.EditProfileButton>
      </div> */}
    </Styles.HomeSidePanel>
  );
};

export default HomeSidePanel;
